/*eslint  @typescript-eslint/no-use-before-define: 0*/

import { defineModule } from "direct-vuex";
import { moduleActionContext, moduleGetterContext } from "@/store/index";
import {
  getCommissionServiceType,
  getServiceTypesCategorized,
  getServiceTypes,
  getServiceTypesForDocumentType,
  getTradeTermTypes,
  getServiceTypesSimple,
} from "@/requests/serviceType";
import {
  ServiceTypeListItem,
  ServiceTypesCategorizedResponse,
  ServiceTypesResponse,
} from "@/models/api/serviceTypes/ServiceTypeListItem";
import i18n from "@/plugins/i18n";
import { DataOptions } from "vuetify/types";
import { FILTER_NAMES } from "@/static/filterNames";
import { SelectItem } from "@/models/api/SelectItem";
import { IntKeyValue } from "@/models/api/IntKeyValue";
import { filterCapitalize } from "@/filters/capitalize";
import { SERVICE_CATEGORY } from "@/static/serviceCategory";
import { CriterionBase } from "@/models/api/filters/criterions/CriterionBase";
import { DefaultDataOptions } from "@/models/components/dataTables/DataTableOptions";

export interface ServiceTypesStateInterface {
  serviceTypes: Array<SelectItem> | null;
  serviceTypesCategorized: ServiceTypesCategorizedResponse | null;
  tradeTermTypes: Array<IntKeyValue> | null;
  commissionBaseId: number | null;
  commissions: Array<SelectItem> | null;
  serviceTypesPaged: Array<ServiceTypeListItem> | null;
  serviceTypesCount: number;
  serviceTypesFilters: Array<CriterionBase> | null;
  serviceTypeDataTableOptions: DataOptions;
  isServiceTypesLoading: boolean;
  serviceTypesAll: Array<ServiceTypeListItem> | null;
  serviceTypeDataTableOptionsTemporary: DataOptions;
}

export const ServiceTypesState: ServiceTypesStateInterface = {
  serviceTypes: null,
  serviceTypesCategorized: null,
  tradeTermTypes: null,
  commissionBaseId: null,
  commissions: null,
  serviceTypesPaged: null,
  serviceTypesCount: -1,
  serviceTypesFilters: null,
  serviceTypeDataTableOptions: new DefaultDataOptions(),
  isServiceTypesLoading: false,
  serviceTypesAll: null,
  serviceTypeDataTableOptionsTemporary: new DefaultDataOptions(),
};

const ServiceTypesModule = defineModule({
  state: ServiceTypesState as ServiceTypesStateInterface,
  namespaced: true as const,
  getters: {},
  mutations: {
    clearCache(state) {
      state.serviceTypes = null;
      state.tradeTermTypes = null;
      state.serviceTypesCategorized = null;
      state.tradeTermTypes = null;
      state.commissionBaseId = null;
      state.commissions = null;
      state.serviceTypesAll = null;
    },
    setServiceTypes(state, param: Array<SelectItem> | null) {
      state.serviceTypes = param;
    },
    setTradeTermTypes(state, param: Array<IntKeyValue> | null) {
      state.tradeTermTypes = param;
    },
    setServiceTypesCategorized(state, param: ServiceTypesCategorizedResponse) {
      state.serviceTypesCategorized = param;
    },
    setCommissionServiceTypes(state, param: { baseServiceId: number | null; serviceTypes: Array<SelectItem> | null }) {
      state.commissionBaseId = param.baseServiceId;
      state.commissions = param.serviceTypes;
    },
    setServiceTypesLoading(state, param: boolean) {
      state.isServiceTypesLoading = param;
    },
    setServiceTypesFilters(state, param: Array<CriterionBase> | null) {
      state.serviceTypesFilters = param;
    },
    setServiceTypesResponse(state, param: ServiceTypesResponse) {
      state.serviceTypesPaged = param.items;
      state.serviceTypesCount = param.totalCount;
      state.isServiceTypesLoading = false;
    },
    setServiceTypesResponseTemporary(state, param: Array<ServiceTypeListItem>) {
      state.serviceTypesAll = param;
    },
    setServiceDataTableOptions(state, param: DataOptions) {
      state.serviceTypeDataTableOptions = param;
    },
    setServiceDataTableOptionsTemporary(state, param: DataOptions) {
      state.serviceTypeDataTableOptionsTemporary = param;
    },
  },
  actions: {
    async downloadServiceTypes(context, category: SERVICE_CATEGORY) {
      const { commit, state } = _moduleActionContext(context);
      if (state.serviceTypes === null) {
        const response = await getServiceTypesSimple(category);
        commit.setServiceTypes(response);
      }
    },
    async downloadServiceTypesCategorized(context, gdsId: number) {
      const { commit } = _moduleActionContext(context);
      const response = await getServiceTypesCategorized(gdsId);
      commit.setServiceTypesCategorized(response);
    },
    async downloadServiceTypesPaginated(context, category: SERVICE_CATEGORY) {
      const { commit, state } = _moduleActionContext(context);
      commit.setServiceTypesLoading(true);
      const response = await getServiceTypes(category, {
        count: state.serviceTypeDataTableOptions.itemsPerPage,
        page: state.serviceTypeDataTableOptions.page - 1,
        criterions: state.serviceTypesFilters ?? [],
        filterName: FILTER_NAMES.ServiceTypeSaleFilter,
      });
      commit.setServiceTypesResponse(response);
    },
    async downloadServiceTypesForDocumentType(context, documentTypeId: number) {
      const { commit, state } = _moduleActionContext(context);
      commit.setServiceTypesLoading(true);
      const response = await getServiceTypesForDocumentType(documentTypeId, {
        count: state.serviceTypeDataTableOptions.itemsPerPage,
        page: state.serviceTypeDataTableOptions.page - 1,
        criterions: state.serviceTypesFilters ?? [],
        filterName: FILTER_NAMES.ServiceTypeForDocumentTypeFilter,
      });
      commit.setServiceTypesResponse(response);
    },
    async downloadServiceTypesAll(context, param: boolean) {
      const { commit, state } = _moduleActionContext(context);
      let criterions: Array<CriterionBase> | null = [];
      if (param) criterions = state.serviceTypesFilters ?? [];
      else criterions = [];

      const response = await getServiceTypes(SERVICE_CATEGORY.Sale, {
        count: state.serviceTypeDataTableOptionsTemporary.itemsPerPage,
        page: state.serviceTypeDataTableOptionsTemporary.page - 1,
        criterions: criterions,
        filterName: FILTER_NAMES.ServiceTypeSaleFilter,
      });
      commit.setServiceTypesResponseTemporary(response.items);
    },
    async downloadTradeTermTypes(context) {
      const { commit, state } = _moduleActionContext(context);
      if (state.tradeTermTypes === null) {
        const response = await getTradeTermTypes();
        commit.setTradeTermTypes(response);
      }
    },
    async getBaseCommissionServiceType(context, serviceId: number) {
      const { commit, state, dispatch } = _moduleActionContext(context);
      commit.setCommissionServiceTypes({ serviceTypes: null, baseServiceId: null });
      await dispatch.downloadServiceTypes(SERVICE_CATEGORY.Sale);

      const first: SelectItem = { value: null } as SelectItem;
      const commission = await getCommissionServiceType(serviceId);
      if (commission !== null)
        first.text = `${filterCapitalize(i18n.t("fromBaseService"))} (${commission.name} (${commission.vat}))`;
      else first.text = filterCapitalize(i18n.t("baseServiceNotSet"));

      const modifiedList = new Array<SelectItem>();
      modifiedList.push(first);
      state.serviceTypes?.forEach((x) => {
        modifiedList.push(x);
      });
      commit.setCommissionServiceTypes({
        serviceTypes: modifiedList,
        baseServiceId: serviceId,
      });
    },
    async getCommissionServiceType(context, baseServiceTypeId: number | null): Promise<number | null> {
      const { commit, state, dispatch } = _moduleActionContext(context);
      commit.setCommissionServiceTypes({ serviceTypes: null, baseServiceId: null });
      await dispatch.downloadServiceTypes(SERVICE_CATEGORY.Sale);

      let overrideServiceId: number | null = null;
      const first: SelectItem = { value: null } as SelectItem;
      if (baseServiceTypeId !== null) {
        const commission = await getCommissionServiceType(baseServiceTypeId);
        if (typeof commission === "object" && commission !== null) {
          first.value = commission.id;
          first.text = `${filterCapitalize(i18n.t("fromBaseService"))} (${commission.name} (${commission.vat}))`;
          overrideServiceId = commission.id;
        } else first.text = filterCapitalize(i18n.t("baseServiceNotSet"));
      } else first.text = filterCapitalize(i18n.t("inheritFromTheUnderlyingService"));

      const modifiedList = new Array<SelectItem>();
      modifiedList.push(first);
      state.serviceTypes?.forEach((x) => {
        modifiedList.push(x);
      });
      commit.setCommissionServiceTypes({ serviceTypes: modifiedList, baseServiceId: baseServiceTypeId });
      return overrideServiceId;
    },
  },
});

export default ServiceTypesModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, ServiceTypesModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, ServiceTypesModule);
